import React, { useState, useCallback, useEffect, useRef } from 'react';
import AvatarContainer from '@/components/AvatarContainer';
import SettingsPanel from '@/components/SettingsPanel';
import { useScene } from '@/lib/useCommon';
import { useConnectionManager } from '@/lib/hooks/useConnectionManager';
import { useVosk } from '@/lib/hooks/useVosk';
import { useAppSettings } from '@/lib/hooks/useAppSettings';
import logger from '@/utils/logger';
import { useCloudSpeechRecognition } from '@/lib/hooks/useCloudSpeechRecognition';
import { DEFAULT_WAKE_WORDS } from '@/utils/wakeWordDetector';
import { useLayout, buildURLWithLayout } from '@/lib/useLayout';
import { getLayout3SceneTitle, getLayout3SceneTitleImage } from '@/utils/layout3Display';
import BottomControls from '../BottomControls';
import ChatLayout from '../ChatLayout';

import style from './index.module.less';

// 全局类型声明已移至 useCloudSpeechRecognition 中

export default function EntryStandby() {
  // 检测是否为第二套主题
  // const isLayout2 = new URLSearchParams(window.location.search).get('layout2') === 'true';

  // 使用统一的设置管理
  const { settingsConfig, recognitionMode } = useAppSettings();

  const [isSupported, setIsSupported] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [voskStatus, setVoskStatus] = useState(''); // Vosk状态

  const isMountedRef = useRef(true);
  const hasAutoStarted = useRef(false); // 防止重复自动开始

  // 控制推荐卡片渐入渐出
  // 可外部控制的显隐状态
  const [recommendVisible, setRecommendVisible] = useState(false); // 默认false，延迟显示
  const [shouldRenderRecommend, setShouldRenderRecommend] = useState(true);
  // 初始渐入
  useEffect(() => {
    const timer = setTimeout(() => setRecommendVisible(true), 100); // 延迟100ms触发动画
    return () => clearTimeout(timer);
  }, []);
  // 显隐切换时，渐出动画结束后再卸载
  useEffect(() => {
    if (recommendVisible) setShouldRenderRecommend(true);
  }, [recommendVisible]);
  const handleRecommendTransitionEnd = () => {
    if (!recommendVisible) setShouldRenderRecommend(false);
  };

  // 响应式：空间不足时自动关闭推荐卡片
  useEffect(() => {
    let timer: number | null = null;
    function handleResize() {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (window.innerWidth < 1000) {
          setRecommendVisible(false);
        } else {
          setRecommendVisible(true);
        }
      }, 100);
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      if (timer) window.clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Redux
  const sceneConfig = useScene();
  const { isInterruptMode } = sceneConfig;

  // “按住说话”模式开关

  // URL 解析改为本地解析，URL→Store 的场景设置由上层 MainPage 统一处理
  const urlParams = new URLSearchParams(window.location.search);
  const autoStartFromURL = urlParams.get('AutoStart') === 'true';
  const sceneId = (() => {
    const match = window.location.pathname.match(/\/scene\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : '';
  })();

  const [recommendQuestions, setRecommendQuestions] = useState<string[]>([]);
  const [wakeUpText, setWakeUpText] = useState('对我说"你好！"唤醒我吧！'); // 默认唤醒文本
  const [wakeWords, setWakeWords] = useState<string[]>(DEFAULT_WAKE_WORDS); // 唤醒词列表，支持场景配置

  // 使用统一的连接管理器
  const { connect, isConnecting } = useConnectionManager();

  // 统一清理函数
  const cleanup = useCallback(() => {
    logger.info('[EntryStandby] 清理资源');
  }, []);

  // 统一唤醒成功后的回调
  const handleWakeWordDetected = useCallback(() => {
    logger.info('[EntryStandby] 唤醒成功！进入对话模式');

    // 连接
    connect()
      .then((result) => {
        if (result.success) {
          logger.info('[EntryStandby] 语音唤醒连接成功');
        } else {
          logger.error('[EntryStandby] 语音唤醒连接失败:', result.error);
        }
      })
      .catch((error) => {
        logger.error('[EntryStandby] 语音唤醒连接异常:', error);
      });
  }, [connect]);

  // 本地识别 (Vosk.js) - 使用动态唤醒词
  const {
    status: voskInternalStatus,
    start: startVosk,
    stop: stopVosk,
  } = useVosk({
    onWakeWordDetected: handleWakeWordDetected,
    wakeWords,
    enabled: recognitionMode === 'local',
    enableHomophone: true,
  });

  // 云端识别 (SpeechRecognition) - 使用动态唤醒词
  const { status: cloudStatus, start: startCloud } = useCloudSpeechRecognition({
    onWakeWordDetected: handleWakeWordDetected,
    wakeWords,
    enabled: recognitionMode === 'cloud',
    enableHomophone: true,
  });

  // 统一状态管理
  const currentStatus = recognitionMode === 'local' ? voskInternalStatus : cloudStatus;
  const startRecognition = recognitionMode === 'local' ? startVosk : startCloud;

  // 更新状态用于UI显示
  useEffect(() => {
    setVoskStatus(currentStatus);
  }, [currentStatus]);

  // 添加调试日志，确保状态同步
  useEffect(() => {
    logger.debug('[EntryStandby] 语音打断状态更新:', {
      isInterruptMode,
      sessionStorage:
        typeof window !== 'undefined' ? sessionStorage.getItem('voiceInterruptAllowed') : null,
    });
  }, [isInterruptMode]);

  // 检查浏览器支持
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSupported(!!SpeechRecognition);
    logger.debug('[EntryStandby] 浏览器语音识别支持:', !!SpeechRecognition);
  }, []);

  // 请求权限
  const requestPermission = useCallback(async () => {
    if (!isSupported) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setPermissionGranted(true);
      logger.info('[EntryStandby] 麦克风权限获取成功');
    } catch (error) {
      logger.error('[EntryStandby] 麦克风权限获取失败:', error);
      setPermissionGranted(false);
    }
  }, [isSupported]);

  // 初始化音频系统（为云端识别预先准备）
  const initAudioSystem = useCallback(async () => {
    if (!isSupported || isConnecting || !permissionGranted) return;

    try {
      logger.debug('[EntryStandby] 初始化音频系统');

      // 对于云端模式，我们只需要确保权限已获取
      // SpeechRecognition API 会自己处理音频流
      setIsMonitoring(true);
      logger.debug('[EntryStandby] 音频系统初始化完成（云端模式）');
    } catch (error) {
      logger.error('[EntryStandby] 音频系统初始化失败:', error);
      setIsMonitoring(false);
    }
  }, [isSupported, isConnecting, permissionGranted]);

  // 初始化
  useEffect(() => {
    let initTimer: number | null = null;
    const init = async () => {
      if (!permissionGranted) {
        await requestPermission();
      }

      // 延迟初始化，确保权限已获取
      initTimer = window.setTimeout(() => {
        if (permissionGranted && isMountedRef.current) {
          // 启动对应的识别模式
          if (recognitionMode === 'local') {
            // 本地模式使用Vosk，会自动处理音频
            startRecognition();
            setIsMonitoring(true); // 标记为正在监控
          } else {
            // 云端模式需要初始化音频系统
            initAudioSystem().then(() => {
              startRecognition();
            });
          }
        }
      }, 1000);
    };

    init();
    return () => {
      if (initTimer) {
        window.clearTimeout(initTimer);
        initTimer = null;
      }
    };
  }, [permissionGranted, requestPermission, initAudioSystem, startRecognition, recognitionMode]);

  // 组件卸载清理（停掉本地识别）
  useEffect(() => {
    return () => {
      logger.info('[EntryStandby] 组件卸载，清理资源');
      try {
        stopVosk?.();
      } catch {}
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup, stopVosk]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        // 直接调用API获取完整的场景配置，避免重复请求
        if (sceneId) {
          const { default: Apis } = await import('@/app/index');
          const data = await Apis.Basic.getSceneSuggestions({ scene: sceneId });

          // 处理建议问题
          let questions = [
            '我们学校的历史是什么？',
            '五年级数学有哪些重点？',
            '请写一首古诗',
            '你最擅长做什么？',
          ];

          if (!Array.isArray(data) && data) {
            // 提取建议问题
            if (data.suggestions || data.suggestionQuestions) {
              const suggestions = data.suggestions || data.suggestionQuestions;
              if (Array.isArray(suggestions)) {
                questions = suggestions
                  .map((q: any) => (typeof q === 'string' ? q : q?.text))
                  .filter(Boolean);
              }
            }

            // 处理唤醒文本配置
            const customWakeUpText = data?.wakeUpText || data?.SceneConfig?.wakeUpText;
            if (customWakeUpText && typeof customWakeUpText === 'string') {
              setWakeUpText(customWakeUpText);
              logger.info('[EntryStandby] 唤醒文本已设置:', customWakeUpText);
            }

            // 处理唤醒词配置（支持场景自定义唤醒词）
            const customWakeWords = data?.wakeWords || data?.SceneConfig?.wakeWords;
            if (Array.isArray(customWakeWords) && customWakeWords.length > 0) {
              // 合并默认唤醒词和自定义唤醒词，去重
              const mergedWakeWords = [...new Set([...customWakeWords, ...DEFAULT_WAKE_WORDS])];
              setWakeWords(mergedWakeWords);
              logger.info('[EntryStandby] 唤醒词已设置:', {
                自定义: customWakeWords,
                合并后: mergedWakeWords,
              });
            }

            // 动态主题注入（如果后端携带）
            try {
              // CSS 变量
              const themeCss: Record<string, string> | undefined =
                data?.theme?.css || data?.themeCss;
              if (themeCss && typeof themeCss === 'object') {
                Object.entries(themeCss).forEach(([key, val]) => {
                  if (typeof key === 'string' && typeof val === 'string') {
                    document.documentElement.style.setProperty(key, val);
                  }
                });
              }

              // 背景图片支持
              const backgroundImage = data?.backgroundImage || data?.theme?.backgroundImage;
              if (backgroundImage && typeof backgroundImage === 'string') {
                const backgroundUrl = backgroundImage.startsWith('http')
                  ? backgroundImage
                  : `/${backgroundImage}`;
                const themeHero = `url("${backgroundUrl}") center center / cover no-repeat, radial-gradient(ellipse at 20% 10%, #2ec7a7 0%, #1e3c72 60%, #2a5298 100%), linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)`;

                // 缓存背景图片配置到sessionStorage（按场景命名空间）
                sessionStorage.setItem(`theme:${sceneId}:bg`, backgroundUrl);
                sessionStorage.setItem(`theme:${sceneId}:hero`, themeHero);

                logger.info('[EntryStandby] 背景图片已注入:', backgroundUrl);
              }

              // 处理数字人视频映射
              const videos: any = data?.theme?.videos || data?.avatarVideos || data?.videos;

              if (videos && typeof videos === 'object') {
                const payload: Partial<
                  Record<'greet' | 'default' | 'talk' | 'think' | 'listen', string>
                > = {};
                ['greet', 'default', 'talk', 'think', 'listen'].forEach((k) => {
                  if (typeof videos[k] === 'string' && videos[k]) {
                    // 处理相对路径，添加前缀斜杠
                    const videoPath =
                      videos[k].startsWith('http') || videos[k].startsWith('/')
                        ? videos[k]
                        : `/${videos[k]}`;
                    (payload as any)[k] = videoPath;
                  }
                });

                if (Object.keys(payload).length > 0) {
                  sessionStorage.setItem(`theme:${sceneId}:videos`, JSON.stringify(payload));
                  logger.info('[EntryStandby] 主题视频映射已注入:', payload);
                }
              }

              // 处理主题配置整合（包括校徽）
              const themeConfigData: any = {
                sceneProcessed: true, // 标记已处理场景配置
              };

              // 校徽图片
              const logoImage = data?.logoImage || data?.theme?.logoImage;
              if (logoImage && typeof logoImage === 'string') {
                themeConfigData.logoImage = logoImage;
              }

              // 保存主题配置到 sessionStorage（按场景命名空间，始终保存，即使只有标记）
              sessionStorage.setItem(`theme:${sceneId}:config`, JSON.stringify(themeConfigData));
              // 通知同页 Hook 刷新（sessionStorage 同页变更不会触发 storage 事件）
              window.dispatchEvent(new CustomEvent('theme-config-updated'));
              logger.info('[EntryStandby] 主题配置已注入:', themeConfigData);
            } catch (e) {
              logger.warn('[EntryStandby] 主题配置注入失败(忽略继续):', e);
            }
          }

          setRecommendQuestions(questions);
        } else {
          // 没有场景ID时使用默认问题
          setRecommendQuestions([
            '我们学校的历史是什么？',
            '五年级数学有哪些重点？',
            '请写一首古诗',
            '你最擅长做什么？',
          ]);
        }
      } catch (error) {
        logger.error(`[EntryStandby] 获取建议问题失败:`, error);
        // 使用默认问题作为回退
        setRecommendQuestions([
          '我们学校的历史是什么？',
          '五年级数学有哪些重点？',
          '请写一首古诗',
          '你最擅长做什么？',
        ]);
      }
    };

    fetchSuggestions();
  }, [sceneId]);

  // 手动进入
  const handleClickJoin = useCallback(() => {
    if (isConnecting) return;

    logger.debug('[EntryStandby] 手动进入对话');

    // 使用统一的连接管理器
    connect()
      .then((result) => {
        if (result.success) {
          logger.debug('[EntryStandby] 手动进入对话连接成功');
        } else {
          logger.debug('[EntryStandby] 手动进入对话连接失败:', result.error);
        }
      })
      .catch((error) => {
        logger.debug('[EntryStandby] 手动进入对话连接异常:', error);
      });
  }, [isConnecting, connect]);

  // 从当前URL提取场景名
  function getSceneFromURL() {
    const match = window.location.pathname.match(/\/scene\/([^/?#]+)/i);
    return match ? decodeURIComponent(match[1]) : 'Custom';
  }
  const sceneName = getSceneFromURL();

  // 跳转函数 - 使用统一的布局URL构建，保持原有参数格式
  const handleQuestionClick = (question: string) => {
    const params = new URLSearchParams();
    params.set('Question', question);
    params.set('DirectRoom', 'true');

    // 使用统一的URL构建函数，自动保持原有的布局参数格式（路径或查询参数）
    const url = buildURLWithLayout(`/scene/${encodeURIComponent(sceneName)}`, sceneName, params);
    window.location.href = url;
  };

  // 状态文本
  const getStatusText = () => {
    if (isConnecting) return '连接中...';

    // 根据识别模式显示状态
    switch (voskStatus) {
      case 'loading':
        return recognitionMode === 'local' ? '本地引擎加载中...' : '云端引擎初始化中...';
      case 'loading_model':
        return '正在加载识别模型...';
      case 'ready':
      case 'listening':
        return `请说出唤醒词...`;
      case 'error':
        return `${recognitionMode === 'local' ? '本地' : '云端'}识别出错，请刷新。`;
      case 'permission-denied':
        return '需要麦克风权限，请允许并刷新页面。';
      case 'disabled':
        return '语音识别已禁用';
      default:
        return '准备中...';
    }
  };

  const getMergedWakeText = () => {
    const base = (wakeUpText || '').trim();
    // 如果已有“点击”或“进入对话”字样，避免重复
    const hasClickHint = /点击|进入对话/.test(base);
    const text = hasClickHint ? base : `${base.replace(/！?$/, '')} 或点击我进入对话`;
    return text;
  };

  // 自动开始对话逻辑（依赖isMonitoring，无需定时器）
  useEffect(() => {
    if (
      autoStartFromURL &&
      !hasAutoStarted.current &&
      !isConnecting &&
      permissionGranted &&
      isMonitoring
    ) {
      logger.debug('[EntryStandby] 检测到自动开始参数，准备自动进入对话');
      hasAutoStarted.current = true;
      handleClickJoin();
    }
  }, [autoStartFromURL, isConnecting, permissionGranted, isMonitoring, handleClickJoin]);

  // 使用统一的布局检测 hook
  const { chatLayoutMode, isLayout3 } = useLayout();
  const layout3SceneTitle = isLayout3 ? getLayout3SceneTitle(sceneId, sceneConfig.name) : '';
  const layout3SceneTitleImage = isLayout3 ? getLayout3SceneTitleImage(sceneId) : '';

  const welcomeBubble = (
    <div className={style.standbyWelcomeBubble}>
      <div className={`${style.cloudDecor} rtc-cloud-decor`}>
        <div className={`${style.cloudDot1} rtc-cloud-dot`} />
        <div className={`${style.cloudDot2} rtc-cloud-dot`} />
        <div className={`${style.cloudDot3} rtc-cloud-dot`} />
      </div>
      <div>{getMergedWakeText()}</div>
      <div className={style.standbyTip}>{getStatusText()}</div>
      {!isSupported && (
        <div className={`${style.standbyTip} ${style.standbyTipWarn}`}>
          建议使用 Chrome、Edge 等现代浏览器
        </div>
      )}
    </div>
  );

  return (
    <ChatLayout
      mode={chatLayoutMode}
      avatar={
        <AvatarContainer
          mode="standby"
          showUserTag={false}
          onClick={handleClickJoin}
          disabled={isConnecting}
          showShadow
          className={isConnecting ? style.avatarDisabled : style.avatarClickable}
        />
      }
      inputBox={<BottomControls mode="entry" onStart={handleClickJoin} />}
      bubbles={
        shouldRenderRecommend ? (
          <div
            className={[style.recommendCard, recommendVisible && style.show]
              .filter(Boolean)
              .join(' ')}
            onTransitionEnd={handleRecommendTransitionEnd}
            data-recommend-card
          >
            <div className={style.recommendTitle}>推荐问题</div>
            <div className={style.recommendList}>
              {recommendQuestions.map((q) => (
                <button
                  key={q}
                  className={style.recommendItem}
                  onClick={() => handleQuestionClick(q)}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : null
      }
    >
      {welcomeBubble}
      {layout3SceneTitle && (
        <div className={layout3SceneTitleImage ? 'rtc-layout3-avatar-title rtc-layout3-avatar-title--image' : 'rtc-layout3-avatar-title'} aria-hidden>
          {layout3SceneTitleImage ? (
            <img className="rtc-layout3-avatar-title-image" src={layout3SceneTitleImage} alt="" />
          ) : (
            <span className="rtc-layout3-avatar-title-text">{layout3SceneTitle}</span>
          )}
        </div>
      )}
      {/* 语音设置开关区域 */}
      <div className={style.toggleContainer}>
        <SettingsPanel
          items={settingsConfig}
          className={style.settingsBtnWrapper}
          buttonClassName={style.settingsBtn}
          position="br"
        />
      </div>
    </ChatLayout>
  );
}
